import { Historic, HistoricAnswer } from 'store/states/historics/types';
import { t } from 'i18next';
import { ExportToCsv } from 'export-to-csv';
import { AnswerSubfield, Field, Form, Item } from 'store/states/forms/types';
import { collapseLineBreaks } from './collapseLineBreaks';
import { formatTimestampDate } from './formatters';

const hideFields = ['image', 'draw', 'signature'];

export interface Column {
  key: string;
  parentFieldId: number;
  // explode=true: a coluna tem 1 valor por item selecionado; o campo vira uma
  // dimensao do produto cartesiano (checkbox / checkbox_group). explode=false:
  // valor escalar, repetido fixo em todas as linhas do registro.
  explode: boolean;
  getValues: (
    answer: HistoricAnswer | undefined,
    locale: string,
  ) => string[];
}

function getLabelByValue(items?: Item[] | null, value?: string | null): string {
  return items?.find((item) => item.value === value)?.label ?? value ?? '';
}

// Impede o Excel de interpretar o conteudo da celula como formula (#NOME?).
// Celulas iniciadas por = + - @ tab ou CR sao avaliadas como formula pelo Excel;
// prefixamos com aspa simples (invisivel na planilha) para forcar texto puro.
function sanitizeCsvCell(value: string): string {
  if (value && /^[=+\-@\t\r]/.test(value)) {
    return `'${value}`;
  }
  return value;
}

function formatPlainAnswer(
  answer: HistoricAnswer | undefined,
  field: Field,
  locale: string,
): string {
  if (answer === undefined || answer.answer === null || answer.answer === '') {
    return '';
  }

  if (field.type === 'date') {
    return formatTimestampDate(answer.answer as string, locale);
  }

  if (field.type === 'radio' && typeof answer.answer === 'string') {
    return getLabelByValue(field.items, answer.answer);
  }

  return String(answer.answer);
}

// Checkbox simples. Dois formatos de answer possiveis:
// - Fill novo: string tab-delimitada, onde o 1o campo e' descartado (slice(1))
//   e o restante sao os values marcados.
// - Historico EDITADO: generateUpdateAnswersData converte a string num array de
//   values (ja sem o prefixo tab). Sem tratar o array, o checkbox some do export
//   (era o bug do "Numero do contrato e area").
// Retorna a lista de labels; cada item vira uma dimensao do produto cartesiano.
function getCheckboxValues(
  answer: HistoricAnswer | undefined,
  field: Field,
): string[] {
  if (!answer || answer.answer == null || answer.answer === '') {
    return [];
  }
  const rawValues = Array.isArray(answer.answer)
    ? (answer.answer as unknown as string[])
    : answer.answer.split('\t').slice(1);
  return rawValues.map((v) => getLabelByValue(field.items, v));
}

function parseSubfields(raw: string | AnswerSubfield[]): AnswerSubfield[] {
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as AnswerSubfield[]) : [];
    } catch {
      return [];
    }
  }
  return Array.isArray(raw) ? raw : [];
}

function getColumnsForField(field: Field): Column[] {
  if (hideFields.includes(field.type)) return [];

  if (field.type === 'checkbox_group') {
    const parentLabel = String(field.summary_title ?? '');

    // Coluna-pai: 1 label por item marcado. Nao filtra vazios para manter o
    // alinhamento posicional com as subcolunas (mesmo indice = mesmo item).
    const parentColumn: Column = {
      key: parentLabel,
      parentFieldId: field.field_id,
      explode: true,
      getValues: (answer) => {
        if (!answer || !answer.answer) return [];
        const subfields = parseSubfields(
          answer.answer as string | AnswerSubfield[],
        );
        return subfields.map((item) => getLabelByValue(field.items, item.value));
      },
    };

    const subColumns: Column[] = (field.sub_fields ?? []).map((subDef) => ({
      key: parentLabel
        ? `${parentLabel} - ${subDef.summary_title ?? ''}`
        : String(subDef.summary_title ?? ''),
      parentFieldId: field.field_id,
      explode: true,
      getValues: (answer) => {
        if (!answer || !answer.answer) return [];
        const subfields = parseSubfields(
          answer.answer as string | AnswerSubfield[],
        );

        return subfields.map((item) => {
          const matchingSub = (item.sub_fields ?? []).find(
            (s) => (s as any).field_id === subDef.field_id,
          );
          if (!matchingSub) return '';
          return getLabelByValue(
            subDef.items,
            (matchingSub as any).value ?? '',
          );
        });
      },
    }));

    return [parentColumn, ...subColumns];
  }

  if (field.type === 'checkbox') {
    return [
      {
        key: String(field.summary_title ?? ''),
        parentFieldId: field.field_id,
        explode: true,
        getValues: (answer) => getCheckboxValues(answer, field),
      },
    ];
  }

  return [
    {
      key: String(field.summary_title ?? ''),
      parentFieldId: field.field_id,
      explode: false,
      getValues: (answer, locale) => [formatPlainAnswer(answer, field, locale)],
    },
  ];
}

export function getAllColumns(form: Form): Column[] {
  return form.fields.flatMap(getColumnsForField);
}

export function createHeader(form: Form) {
  return [
    t('form_name'),
    t('username'),
    t('registration_date'),
    ...getAllColumns(form).map((c) => c.key),
  ];
}

// Nome do arquivo de exportacao = titulo do formulario, normalizado: remove
// acentos, mantem SOMENTE letras, separa palavras por "_" e deixa minusculo.
// Cai para "notes" quando nada sobra. Compartilhado por CSV e XLSX.
export function getExportFilename(form: Form): string {
  const normalized = (form.title ?? '')
    .normalize('NFD') // separa letra + diacritico (ex: "á" -> "a" + acento)
    .replace(/[̀-ͯ]/g, '') // remove os diacriticos
    .toLowerCase()
    .replace(/[^a-z]+/g, '_') // tudo que nao e letra vira separador
    .replace(/^_+|_+$/g, ''); // remove "_" das pontas
  return normalized || 'notes';
}

export function setOptionsCsv(csvFilename: string, form: Form) {
  return {
    filename: csvFilename,
    fieldSeparator: ';',
    quoteStrings: '"',
    decimalSeparator: ',',
    showLabels: true,
    useTextFile: false,
    useBom: true,
    useKeysAsHeaders: false,
    headers: createHeader(form),
  };
}

// Constroi as linhas CRUAS (sem sanitize). Fonte unica de dados compartilhada
// entre a exportacao CSV e a XLSX. O XLSX nao precisa do guard de formula porque
// escreve celulas como string (o Excel nao avalia como formula).
//
// PRODUTO CARTESIANO: cada CAMPO multi-valor (checkbox ou checkbox_group) e' uma
// dimensao. As linhas do registro sao todas as combinacoes das dimensoes (sem
// lacunas). Ex.: campo A com 2 itens e B com 3 itens => 2 x 3 = 6 linhas. Com
// varios campos vira N x N x N (o cliente esta ciente do crescimento).
// - checkbox_group: pai + subcampos compartilham a MESMA dimensao (andam juntos
//   por item, o subcampo i pertence ao item i do pai; nao multiplicam entre si).
// - campos escalares (metadados + demais tipos) repetem FIXO em todas as linhas.
// - dimensao vazia conta como 1 (slot unico vazio) pra nao zerar o produto;
//   registro sem nenhum item gera 1 linha.
export function buildRows(
  historics: Historic[],
  form: Form,
  locale: string,
): Record<string, string>[] {
  const columns = getAllColumns(form);

  return historics.flatMap((historic) => {
    const meta: Record<string, string> = {
      [t('form_name')]: historic.form_name ?? '',
      [t('username')]: historic.user_name ?? '',
      [t('registration_date')]: formatTimestampDate(historic.form_date, locale),
    };

    const resolved = columns.map((col) => {
      const answer = historic.answers.find(
        (ans) => ans.field_id === col.parentFieldId,
      );
      return { col, values: col.getValues(answer, locale) };
    });

    // Dimensoes do produto cartesiano: uma por CAMPO explodivel (agrupado por
    // parentFieldId, pra o checkbox_group nao virar N dimensoes). Comprimento =
    // qtd de itens (no minimo 1, pra vazio nao zerar o produto).
    const dims: { fieldId: number; length: number }[] = [];
    resolved.forEach(({ col, values }) => {
      if (!col.explode) return;
      const len = Math.max(1, values.length);
      const dim = dims.find((d) => d.fieldId === col.parentFieldId);
      if (dim) dim.length = Math.max(dim.length, len);
      else dims.push({ fieldId: col.parentFieldId, length: len });
    });

    const rowCount = dims.reduce((total, d) => total * d.length, 1);

    return Array.from({ length: rowCount }, (_, r) => {
      // Decodifica o indice linear r em um indice por dimensao (mixed-radix).
      const indexByField = new Map<number, number>();
      let rem = r;
      for (let k = dims.length - 1; k >= 0; k -= 1) {
        indexByField.set(dims[k].fieldId, rem % dims[k].length);
        rem = Math.floor(rem / dims[k].length);
      }

      const row: Record<string, string> = { ...meta };
      resolved.forEach(({ col, values }) => {
        if (col.explode) {
          const i = indexByField.get(col.parentFieldId) ?? 0;
          row[col.key] = collapseLineBreaks(values[i]);
        } else {
          row[col.key] = collapseLineBreaks(values[0]);
        }
      });
      return row;
    });
  });
}

export function formatAnswers(
  historics: Historic[],
  form: Form,
  locale: string,
) {
  return buildRows(historics, form, locale).map((row) => {
    const sanitized: Record<string, string> = {};
    Object.keys(row).forEach((key) => {
      sanitized[key] = sanitizeCsvCell(row[key]);
    });
    return sanitized;
  });
}

export function handleDataToGenerateCsv(
  historics: Historic[],
  form: Form,
  locale: string,
) {
  const answers = formatAnswers(historics, form, locale);
  const options = setOptionsCsv(getExportFilename(form), form);

  const csvExporter = new ExportToCsv(options);
  csvExporter.generateCsv(answers);
}
